import React, { useCallback, useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';

import { useUpdateInvestorProfileMutation } from 'api/main';
import { validateSchema } from 'utils/helpers';
import { emailSchema } from './EditableEmailAddress';
import { texts } from 'utils/languages/fr';

import { CustomTextField, Loader } from 'components';
import { Box, FormHelperText, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';

const useStyles = makeStyles(() => ({
    field: { marginBottom: 0, flex: '0 1 75%' },
}));

const CommunicationEmailAdd = props => {
    const { onAdd, addresses, avoidCallbacks } = props;
    const classes = useStyles();

    const [newAddress, setNewAddress] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const [, { isSuccess }] = useUpdateInvestorProfileMutation({
        fixedCacheKey: 'investor-emails',
    });

    const handleChange = useCallback(e => {
        setNewAddress(e.target.value);
        setError('');
    }, []);

    const handleAdd = useCallback(() => {
        const { valid, newErrors } = validateSchema(emailSchema, newAddress);
        const isDuplicate = addresses.find(a => a === newAddress?.trim?.());
        if (isDuplicate) {
            setError(texts.EMAIL_DUPLICATE);
            return;
        }
        if (valid) {
            setError('');
            if (avoidCallbacks) {
                onAdd(newAddress);
                setNewAddress('');
            } else {
                setLoading(true);
                onAdd(newAddress, () => {
                    setNewAddress('');
                    setLoading(false);
                });
            }
        } else {
            setError(newErrors['']);
        }
    }, [newAddress, onAdd, addresses, avoidCallbacks]);

    const handleKeyUp = useCallback(
        e => {
            if (e.key === 'Enter') {
                handleAdd();
            }
        },
        [handleAdd]
    );

    useEffect(() => {
        if (isSuccess) {
            setNewAddress('');
            setLoading(false);
        }
    }, [isSuccess]);

    return (
        <Box display="flex" alignItems="center" justifyContent="space-between" paddingY={2} mb={2}>
            <CustomTextField
                type="email"
                disabled={loading}
                inputStyle={classes.field}
                placeholder={texts.E_MAIL}
                value={newAddress}
                onChange={handleChange}
                onKeyUp={handleKeyUp}
                error={error}
            />
            <Loader loading={loading} marginTop={0} size={20}>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-end"
                >
                    <IconButton size="small" color="primary" disableRipple onClick={handleAdd}>
                        <icons.Check sx={{ display: 'block' }} />
                    </IconButton>
                    {/* empty error meessage to keep buttons aligned to the text field */}
                    {error && <FormHelperText> </FormHelperText>}
                </Box>
            </Loader>
        </Box>
    );
};

CommunicationEmailAdd.propTypes = {
    onAdd: PropTypes.func.isRequired,
    addresses: PropTypes.arrayOf(PropTypes.string),
    avoidCallbacks: PropTypes.bool,
};

export default CommunicationEmailAdd;
